import { defineStore } from "pinia"
import { apiV1 } from "@/config/axios"

export const useDocumentTypeStore = defineStore("documentType", {
  state: () => ({
    documentTypes: [],
    currentDocumentType: null,
    connectors: [],
    loading: false,
    error: null
  }),

  getters: {
    hasDocumentTypes: (state) => state.documentTypes.length > 0,
    getDocumentTypeById: (state) => (id) => {
      return state.documentTypes.find((type) => type.id === id)
    }
  },

  actions: {
    async fetchDocumentTypes() {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get("/document-types")
        this.documentTypes = response.data.data || response.data
        return this.documentTypes
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchDocumentTypeById(id) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get(`/document-types/${id}`)
        // API returns flat object (no 'data' wrapper) via response()->json(Resource)
        // response.data is the full document type {id, name, description, data: {folio, charge}}
        const documentType = response.data
        this.currentDocumentType = documentType
        return documentType
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchConnectors() {
      this.error = null
      try {
        const response = await apiV1.get("/connectors")
        this.connectors = response.data?.data ?? response.data ?? []
        return this.connectors
      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    async fetchDocumentTypeConnectors(documentTypeId) {
      this.error = null
      try {
        const response = await apiV1.get(
          `/document-types/${documentTypeId}/connectors`
        )
        const list = response.data?.data ?? response.data ?? []
        this.connectors = Array.isArray(list) ? list : []
        return this.connectors
      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    async fetchConnectorDocumentTypes(accountConnectorId) {
      this.error = null
      try {
        const response = await apiV1.get(
          `/connectors/${accountConnectorId}/document-types`
        )
        return response.data?.data ?? response.data ?? []
      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    async fetchConnectorFieldDefinitions(connectorId) {
      this.error = null
      try {
        const response = await apiV1.get(`/connectors/${connectorId}/field-definitions`)
        return response.data?.data ?? null
      } catch (error) {
        this.error = error.message
        throw error
      }
    },

    clearCurrentDocumentType() {
      this.currentDocumentType = null
    },

    clearAll() {
      this.documentTypes = []
      this.currentDocumentType = null
      this.connectors = []
      this.loading = false
      this.error = null
    },

    async createDocumentType(documentData) {
      this.loading = true
      this.error = null
      try {
        const payload = {
          name: documentData.name,
          description: documentData.description,
          status:
            documentData.status !== undefined ? documentData.status : true,
          data: documentData.data ?? {}
        }

        const response = await apiV1.post("/document-types", payload)
        await this.fetchDocumentTypes()
        return response.data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateDocumentType(documentType) {
      this.loading = true
      this.error = null
      try {
        const payload = {
          name: documentType.name,
          description: documentType.description,
          status:
            documentType.status !== undefined ? documentType.status : true,
          data: documentType.data ?? {}
        }

        const response = await apiV1.put(
          `/document-types/${documentType.id}`,
          payload
        )

        await this.fetchDocumentTypes()
        return response.data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async deleteDocumentType(documentType) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.delete(
          `/document-types/${documentType.id}`
        )
        this.documentTypes = this.documentTypes.filter(
          (type) => type.id !== documentType.id
        )
        return response.data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async duplicateDocumentType(id) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.post(`/document-types/${id}/duplicate`)
        await this.fetchDocumentTypes()
        return response.data?.data ?? response.data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateDocumentTypeStatus(documentType) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.patch(
          `/document-types/${documentType.id}/status`,
          { status: documentType.status }
        )

        const index = this.documentTypes.findIndex(
          (type) => type.id === documentType.id
        )

        if (index !== -1) {
          this.documentTypes[index] = response.data.data
        }
        return response.data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async getDocumentTypeTemplate(documentTypeId) {
      this.loading = true
      this.error = null
      try {
        const response = await apiV1.get(
          `/document-types/${documentTypeId}/template`
        )
        return response.data
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async autoMapConnectorFields(documentTypeId, connectorSlug) {
      this.error = null
      try {
        const response = await apiV1.post(
          `/document-types/${documentTypeId}/auto-map-connector`,
          { connector_slug: connectorSlug }
        )
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    },

    async analyzeExample(file) {
      this.error = null
      try {
        const formData = new FormData()
        formData.append("file", file)
        const response = await apiV1.post(
          "/document-types/analyze-example",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000
          }
        )
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || error.message
        throw error
      }
    }
  }
})
